import {
    NinchatEvents,
    NinchatProperties,
} from '../../../../api/types'
import React, {
    useContext, useMemo,
} from 'react'
import {
    details,
    fileContainer,
    fileDocument,
    fileMessageTime,
    fileSize,
    fileSpeechBubble,
    icon,
    info,
    thumbnail,
    thumbnailOverlay,
} from './styles/FileView'
import {
    messageTime,
    speechBubble,
} from '../styles/ChannelMessage'
import {DocumentTextIcon} from '../../../Icon'
import {EmbedContext} from 'components/EmbedContext'
import {FileViewProps} from './types'
import Image from '../../../Image'
import {calculateDimensions} from 'utils/file'
import {describeFile} from 'api/action/describeFile'
import {fileUtil} from '../../../../utils'
import {styled} from '../../../../stitches/config'
import {useAppDispatch} from '../../../../hooks'
import useLightbox from 'hooks/lightbox'
import {useTranslation} from 'react-i18next'

const StyledFile = styled('div', fileContainer)
const StyledThumbnail = styled('div', thumbnail)
const StyledThumbnailOverlay = styled('div', thumbnailOverlay)
const StyledSpeechBubble = styled('div', speechBubble, fileSpeechBubble)
const StyledDocument = styled('div', fileDocument)
const StyledIcon = styled('div', icon)
const StyledInfo = styled('div', info, {overflowWrap: 'anywhere'})
const StyledDetails = styled('div', details)
const StyledSize = styled('span', fileSize)
const StyledTime = styled('time', messageTime, fileMessageTime)

const FileView = (props: FileViewProps): React.ReactElement => {
    const {
        file,
        me,
        repeated,
        size,
        time,
        rights,
    } = props

    const dispatch = useAppDispatch()
    const {t} = useTranslation()
    const {open} = useLightbox()

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const embedThumbnailWidth = embed?.chatThumbnailsSize?.maxWidth
    const embedThumbnailHeight = embed?.chatThumbnailsSize?.maxHeight

    const openFile = (fileId: NinchatProperties.FileID, orginalURL: boolean): void => {
        dispatch(describeFile({file_id: fileId}))
            .unwrap()
            .then(
                (event: NinchatEvents.NinchatFileFoundEvent) => {
                    const url = orginalURL ? event.file_url : event.converted_url ?? event.file_url
                    // For embed queue/channel, open images in lightbox and other files in new tab
                    if (event.file_attrs.type?.startsWith('image/')) {
                        open(url, event.file_attrs)
                    } else {
                        window.open(url, '_blank', 'noopener noreferrer')
                    }
                },
                () => undefined,
            )
    }


    const dimensions = useMemo(() => calculateDimensions(file.file_attrs.thumbnail), [file.file_attrs.thumbnail])
    const openOriginal = t('Open original')
    const fileType = file.file_attrs.type?.split('/')?.[1] ?? ''
    const isConverted = !!file.converted_url
    return (
        <StyledFile>
            {!!file.file_attrs.thumbnail && (
                <a
                    href={`#/x/file/${file.file_id}`}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                        e.preventDefault()
                        openFile(file.file_id, false)
                    }}
                >
                    <StyledThumbnail
                        me={me}
                        repeated={repeated}
                        size={size}
                        style={{
                            height: `${dimensions.height}px`,
                            width: `${dimensions.width}px`,
                            ...isEmbedQueueChannel && embedThumbnailWidth ? {maxWidth: `${embedThumbnailWidth}`} : {},
                            ...isEmbedQueueChannel && embedThumbnailHeight ? {maxHeight: `${embedThumbnailHeight}`} : {},
                        }}
                    >
                        <Image
                            file_id={file.file_id}
                            height={dimensions.height}
                            src={file.thumbnail_url}
                            width={dimensions.width}
                        />
                    </StyledThumbnail>
                    <StyledThumbnailOverlay>
                        <time dateTime={time}>{time}</time>
                        {isConverted ?
                            <span onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                                e.preventDefault()
                                e.stopPropagation() // don't propagate the event to the parent
                                openFile(file.file_id, true) // open the original file
                            }}
                            >
                                {openOriginal} (.{fileType})
                            </span>
                            : null}
                    </StyledThumbnailOverlay>
                </a>
            )}
            {!file.file_attrs.thumbnail && (
                <StyledSpeechBubble isEmbedQueueChannel={isEmbedQueueChannel} me={me} repeated={repeated} rights={rights}>
                    <StyledDocument>
                        <a
                            href={`#/x/file/${file.file_id}`}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                                e.preventDefault()
                                openFile(file.file_id, false)
                            }}
                        >
                            <StyledIcon>
                                <DocumentTextIcon />
                            </StyledIcon>
                        </a>
                        <StyledInfo>
                            <a
                                href={`#/x/file/${file.file_id}`}
                                onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                                    e.preventDefault()
                                    openFile(file.file_id, false)
                                }}
                            >
                                {file.file_attrs.name}
                            </a>
                            <StyledDetails>
                                {!!file.file_attrs.size && (
                                    <StyledSize title={fileUtil.formatBytes(file.file_attrs.size)}>
                                        {fileUtil.formatBytes(file.file_attrs.size)}
                                    </StyledSize>
                                )}
                                <StyledTime dateTime={time} isEmbedQueueChannel={isEmbedQueueChannel} rights={rights}>{time}</StyledTime>
                            </StyledDetails>
                        </StyledInfo>
                    </StyledDocument>
                </StyledSpeechBubble>
            )}
        </StyledFile>
    )
}

export default FileView
